import React, { useRef, useEffect } from 'react';
import './RichTextEditor.css';

export default function RichTextEditor({ value, onChange, placeholder, className = '', onFileAttach, taskId, readOnly = false }) {
  const editorRef = useRef(null);
  const toolbarRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (editorRef.current && value !== undefined) {
      // Only update if content is different to avoid cursor jumping
      if (editorRef.current.innerHTML !== value) {
        editorRef.current.innerHTML = value || '';
      }
    }
  }, [value]);

  const handleInput = () => {
    if (editorRef.current && onChange) {
      onChange(editorRef.current.innerHTML);
    }
  };

  const execCommand = (command, value = null) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
    handleInput();
  };

  const formatText = (command) => {
    execCommand(command);
  };

  const insertLink = () => {
    const url = prompt('Enter URL:');
    if (url) {
      execCommand('createLink', url);
    }
  };

  const insertList = (type) => {
    execCommand(type === 'ordered' ? 'insertOrderedList' : 'insertUnorderedList');
  };

  const handleFileAttach = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    // Trigger click immediately without delay
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0 || !onFileAttach) return;
    
    await onFileAttach(files);
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className={`rich-text-editor-wrapper ${className}`}>
      {!readOnly && (
      <div className="rich-text-toolbar" ref={toolbarRef}>
        <button
          type="button"
          className="toolbar-btn"
          onClick={() => formatText('bold')}
          title="Bold"
        >
          <strong>B</strong>
        </button>
        <button
          type="button"
          className="toolbar-btn"
          onClick={() => formatText('italic')}
          title="Italic"
        >
          <em>I</em>
        </button>
        <button
          type="button"
          className="toolbar-btn"
          onClick={() => formatText('underline')}
          title="Underline"
        >
          <u>U</u>
        </button>
        <button
          type="button"
          className="toolbar-btn"
          onClick={() => formatText('strikeThrough')}
          title="Strikethrough"
        >
          <s>S</s>
        </button>
        <div className="toolbar-separator" />
        <button
          type="button"
          className="toolbar-btn"
          onClick={() => insertList('ordered')}
          title="Numbered List"
        >
          1.
        </button>
        <button
          type="button"
          className="toolbar-btn"
          onClick={() => insertList('bullet')}
          title="Bullet List"
        >
          •
        </button>
        <div className="toolbar-separator" />
        <button
          type="button"
          className="toolbar-btn"
          onClick={insertLink}
          title="Insert Link"
        >
          🔗
        </button>
        {onFileAttach && taskId && (
          <>
            <div className="toolbar-separator" />
            <button
              type="button"
              className="toolbar-btn"
              onClick={handleFileAttach}
              title="Attach File"
            >
              📎
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleFileChange}
              style={{ position: 'absolute', opacity: 0, width: 0, height: 0, pointerEvents: 'none', zIndex: -1 }}
              tabIndex={-1}
            />
          </>
        )}
        <button
          type="button"
          className="toolbar-btn"
          onClick={() => formatText('removeFormat')}
          title="Clear Formatting"
        >
          Clear
        </button>
      </div>
      )}
      <div
        ref={editorRef}
        className="rich-text-editor"
        contentEditable={!readOnly}
        onInput={readOnly ? undefined : handleInput}
        onPaste={(e) => {
          e.preventDefault();
          const text = e.clipboardData.getData('text/plain');
          document.execCommand('insertText', false, text);
          handleInput();
        }}
        data-placeholder={placeholder}
        suppressContentEditableWarning
      />
    </div>
  );
}

